import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { useFetch } from 'hooks';
import { Abbreviation, Country, Date } from 'components/atoms';
import { ActionMenu, Indicator, Pagination, SectionHeading } from 'components/molecules';
import {
  SimpleTable,
  TableWithPaginationContainer,
} from 'pages/third-party-profile/ThirdPartyProfile.styles';
import {
  ActionMenuWrapper,
  LinkedThirdPartiesComponent,
  LinkedThirdPartyName,
} from './LinkedThirdparties.styles';
import LinkThirdpartyModal from './link-thirdparty-modal';
import { GatewayPermissions } from 'auth/constants/permissions';
import { ActionMenuActions, ThirdPartyBasicData } from './types';
import ThirdpartyUnlinkModal from './third-party-unlink-modal';

import generateTheme from 'theme';
import { useUserSettings } from 'context/DataContext';
import {
  LinkedThirdPartyFieldsConfigs,
  PagedResult,
  ThirdPartyCreateModelResponse,
  ThirdPartyType,
} from 'types';
import { ThirdPartyFormModal } from 'components/organisms/third-party-form-modal';
import EditableLabel from 'components/molecules/editable-label';
import { ReactComponent as UnlinkIcon } from 'assets/svg/Icons/Unlink.svg';
import ConfirmationModal from 'components/organisms/confirmation-modal';
import { GatewaySubscriptionTypes } from 'auth/constants/subscriptions';
import { useTerminologyConfig } from 'context/DataContext';
import LinkedThirdPartyHiddenRow from './LinkedThirdPartyHiddenRow';
import LinkedThirdPartyScreeningFindings from './LinkedThirdPartyScreeningFindings';
import { LinkedThirdPartyDetailResponse } from 'types/responses/thirdParty/linkedThirdPartyDetailResponse';
import SkeletonTableBody from 'components/skeleton-table-body';

const theme = generateTheme();

type LinkedThirdpartiesProps = {
  thirdParty: ThirdPartyBasicData;
  numberOfLinkedThirdParties: number;
  linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs;
};

function isPercentageEnabledForType(config: LinkedThirdPartyFieldsConfigs, type: string) {
  if (type === 'Business') {
    return !!config?.isCompanyOwnershipPercentageEnabled;
  }
  if (type === 'Individual') {
    return !!config?.isIndividualOwnershipPercentageEnabled;
  }
  if (type === 'Unknown') {
    return !!config?.isUnknownOwnershipPercentageEnabled;
  }
  return false;
}

const LinkedThirdparties: React.FC<LinkedThirdpartiesProps> = ({
  thirdParty,
  numberOfLinkedThirdParties,
  linkedThirdPartyConfigurations,
}) => {
  const terminologyConfig = useTerminologyConfig();
  const history = useHistory();
  const { tokenInfo } = useAuth();
  const { hasPermission, hasSubscription } = useUserSettings();
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const { vantageWebApi } = useConfig();
  const { id } = useParams<{ id: string }>();
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [thirdpartyToBeUnlink, setThirdpartyToBeUnlinked] =
    useState<LinkedThirdPartyDetailResponse | null>(null);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState<boolean>(false);
  const [isInitiateComplianceCheckModalOpen, setIsInitiateComplianceCheckModalOpen] =
    useState(false);
  const [createdThirdPartyData, setCreatedThirdPartyData] =
    useState<ThirdPartyCreateModelResponse | null>(null);
  const [linkModalState, setLinkModalState] = useState<{
    isOpen: boolean;
    internalId: string | null;
  }>({
    isOpen: false,
    internalId: null,
  });

  const {
    data: linkedThirdpartiesData,
    responseStatus,
    loading,
    error,
    trigger: fetchLinkedThirdparties,
  } = useFetch<PagedResult<LinkedThirdPartyDetailResponse>>(
    `${vantageWebApi}/thirdParty/${id}/linkedThirdParties`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
      lazy: true,
    }
  );

  const linkedThirdparties = linkedThirdpartiesData?.results ?? [];

  useEffect(() => {
    fetchLinkedThirdparties({
      queryParams: {
        page: currentPage,
        pageSize: PAGE_SIZE,
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, id, PAGE_SIZE]);

  const { trigger: updateLinkedThirdParty, loading: updateLinkedThirdPartyLoading } = useFetch(
    `${vantageWebApi}/thirdParty/${id}/link/:linkedEntityId`,
    {
      method: 'PUT',
      lazy: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const canInitiateComplianceCheck =
    (hasSubscription(GatewaySubscriptionTypes.SelfServiceScreening) &&
      hasPermission(GatewayPermissions.InitiateDueDiligenceSelfServiceScreening)) ||
    (hasSubscription(GatewaySubscriptionTypes.ManagedServiceScreening) &&
      hasPermission(GatewayPermissions.InitiateDueDiligenceManagedServiceScreening)) ||
    (hasSubscription(GatewaySubscriptionTypes.ManagedServiceDueDiligence) &&
      hasPermission(GatewayPermissions.OrderManagedDueDiligence)) ||
    (hasSubscription(GatewaySubscriptionTypes.SelfServiceUBO) &&
      hasPermission(GatewayPermissions.OrderOwnershipManagedDueDiligence));

  const handleInitiateComplianceCheckOnProceed = () => {
    history.push(`/initiate-due-diligence/${createdThirdPartyData?.id}`);
    setCreatedThirdPartyData(null);
  };

  const handleInitiateComplianceCheckOnCancel = () => {
    setIsInitiateComplianceCheckModalOpen(false);
    setCreatedThirdPartyData(null);
  };

  const handleThirdPartyCreated = (thirdParty) => {
    setIsOpenCreateModal(false);
    setCreatedThirdPartyData(thirdParty);
    setLinkModalState({ isOpen: true, internalId: thirdParty.internalId });
  };

  const handleOnThirdPartyLinked = (thirdparty) => {
    if (thirdparty) {
      fetchLinkedThirdparties();
    }
    if (canInitiateComplianceCheck && createdThirdPartyData) {
      setIsInitiateComplianceCheckModalOpen(true);
    }
  };

  const handleOnCloseModal = () => {
    setIsOpenCreateModal(false);
  };

  const closeUnlinkModal = () => {
    setUnlinkModalOpen(false);
    setThirdpartyToBeUnlinked(null);
  };

  const closeLinkThirdpartyModal = () => {
    setLinkModalState({ isOpen: false, internalId: null });
  };

  const canDeleteRelatedEntities: boolean = hasPermission(GatewayPermissions.DeleteThirdParty);

  const handleLinkRowEdit = (
    linkedThirdpartyId: string,
    property: 'relationship' | 'percentage',
    value: string | number,
    currentValue: string | number
  ) => {
    if (
      (property === 'percentage' && (isNaN(Number(value)) || Number(value) === currentValue)) ||
      (property === 'relationship' && value === currentValue)
    ) {
      return;
    }

    const request: any = {
      property: property,
      relationship: property === 'relationship' ? value : null,
      percentage: property === 'percentage' ? Number(value) : null,
    };

    updateLinkedThirdParty({
      urlParams: { linkedEntityId: linkedThirdpartyId },
      body: JSON.stringify(request),
    }).then(() => {
      fetchLinkedThirdparties();
    });
  };

  const hasActions: boolean = canDeleteRelatedEntities;

  const getThirdPartyIcon = (type) => {
    switch (type) {
      case ThirdPartyType.Individual:
        return 'user';
      case ThirdPartyType.Business:
        return 'company';
      default:
        return 'unknown';
    }
  };

  const getDueDiligenceAbbreviations = (dueDiligenceTypes: string[]): JSX.Element[] | null => {
    if (!dueDiligenceTypes || dueDiligenceTypes.length === 0) return null;

    const result: JSX.Element[] = [];

    if (dueDiligenceTypes.some((type) => type.includes('Screening'))) {
      result.push(<Abbreviation key="Screening" abbreviation="Screening" />);
    }

    if (dueDiligenceTypes.some((type) => type.includes('DueDiligence'))) {
      result.push(<Abbreviation key="DueDiligence" abbreviation="DueDiligence" />);
    }

    return result.length > 0 ? result : null;
  };

  const getPermittedActions = (linkedThirdparty: LinkedThirdPartyDetailResponse) => {
    const actions = [];
    if (canDeleteRelatedEntities) {
      actions.push({
        icon: 'unlink',
        name: `Unlink ${terminologyConfig?.thirdPartySingularNaming}`,
        onClick: () => {
          setThirdpartyToBeUnlinked(linkedThirdparty);
          setUnlinkModalOpen(true);
        },
      });
    }

    return actions;
  };

  function getLinkThirdpartiesActions(): ActionMenuActions {
    const actions = [];

    if (linkedThirdparties) {
      actions.push({
        name: `Link existing ${terminologyConfig?.thirdPartySingularNaming}`,
        onClick: () => {
          setLinkModalState({ isOpen: true, internalId: null });
        },
      });
    }

    if (hasPermission(GatewayPermissions.CreateThirdParty)) {
      actions.push({
        name: `Create and link a ${terminologyConfig?.thirdPartySingularNaming}`,
        onClick: () => {
          setIsOpenCreateModal(true);
        },
      });
    }

    return actions;
  }

  const canLinkThirdParty =
    hasPermission(GatewayPermissions.CreateThirdParty) ||
    (linkedThirdparties && linkedThirdparties.length > 0);

  const showPercentage =
    linkedThirdPartyConfigurations.isIndividualOwnershipPercentageEnabled ||
    linkedThirdPartyConfigurations.isCompanyOwnershipPercentageEnabled ||
    linkedThirdPartyConfigurations.isUnknownOwnershipPercentageEnabled;

  const handleLinkedThirdPartyNameClick = (linkedThirdparty: LinkedThirdPartyDetailResponse) => {
    if (linkedThirdparty) {
      history.push(`/third-party/profile/${linkedThirdparty.id}`, {
        mainThirdParty: {
          id: thirdParty.thirdpartyId,
          name: thirdParty.name,
        },
        linkedThirdParty: {
          id: linkedThirdparty.id,
          name: linkedThirdparty.name,
        },
      });
    }
  };

  return (
    <LinkedThirdPartiesComponent>
      <SectionHeading
        dark
        border
        text={`Linked ${terminologyConfig?.thirdPartyPluralNaming}${
          linkedThirdpartiesData ? ` (${linkedThirdpartiesData.rowCount})` : ''
        }`}
      >
        {canLinkThirdParty && (
          <ActionMenu
            actions={getLinkThirdpartiesActions()}
            name={`Link a ${terminologyConfig?.thirdPartySingularNaming}`}
            ellipsisColour={theme.colours.white}
            label={`Link a ${terminologyConfig?.thirdPartySingularNaming}`}
          />
        )}
      </SectionHeading>
      {error || (responseStatus && responseStatus !== 200) ? (
        <p>Error loading linked {terminologyConfig?.thirdPartySingularNaming}</p>
      ) : (
        <TableWithPaginationContainer>
          <SimpleTable dark>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Relationship Type</th>
                <th>{showPercentage ? 'Percentage' : null}</th>
                <th>Country</th>
                <th>Created</th>
                <th>DD Type</th>
                <th>Screening Findings</th>
                {canDeleteRelatedEntities && <th></th>}
              </tr>
            </thead>
            <tbody>
              {loading || updateLinkedThirdPartyLoading || !linkedThirdparties ? (
                <SkeletonTableBody
                  columns={6 + (showPercentage ? 1 : 0) + (canDeleteRelatedEntities ? 1 : 0)}
                  rows={Math.min(
                    PAGE_SIZE,
                    numberOfLinkedThirdParties - PAGE_SIZE * (currentPage - 1)
                  )}
                />
              ) : (
                linkedThirdparties.map((linkedThirdparty) => (
                  <tr key={linkedThirdparty.id}>
                    {linkedThirdparty.isVisibleForCurrentUser ? (
                      <>
                        <td>
                          <LinkedThirdPartyName
                            onClick={() => handleLinkedThirdPartyNameClick(linkedThirdparty)}
                          >
                            {linkedThirdparty.name}
                            <Indicator
                              name={linkedThirdparty.name}
                              icon={getThirdPartyIcon(linkedThirdparty.type)}
                            />
                          </LinkedThirdPartyName>
                        </td>
                        <td>{linkedThirdparty.internalId}</td>
                        <td>
                          <EditableLabel
                            value={linkedThirdparty.linkType || null}
                            light
                            onSave={(newValue) =>
                              handleLinkRowEdit(
                                linkedThirdparty.id,
                                'relationship',
                                newValue,
                                linkedThirdparty.linkType
                              )
                            }
                          />
                        </td>
                        {showPercentage &&
                        isPercentageEnabledForType(
                          linkedThirdPartyConfigurations,
                          linkedThirdparty.type
                        ) ? (
                          <td>
                            <EditableLabel
                              value={
                                linkedThirdparty.percentage != null
                                  ? linkedThirdparty.percentage + '%'
                                  : null
                              }
                              inputType="percentage"
                              light
                              onSave={(newValue) =>
                                handleLinkRowEdit(
                                  linkedThirdparty.id,
                                  'percentage',
                                  newValue,
                                  linkedThirdparty.percentage
                                )
                              }
                            />
                          </td>
                        ) : (
                          <td></td>
                        )}
                        <td>
                          {linkedThirdparty.primaryCountry && (
                            <Country countryCode={linkedThirdparty.primaryCountry} />
                          )}
                        </td>
                        <td>
                          {linkedThirdparty.createdDate && (
                            <Date dateTime={linkedThirdparty.createdDate} />
                          )}
                        </td>
                        <td>{getDueDiligenceAbbreviations(linkedThirdparty.dueDiligenceTypes)}</td>
                        <td>
                          <LinkedThirdPartyScreeningFindings
                            thirdPartyId={linkedThirdparty.id}
                            type={linkedThirdparty.type}
                          />
                        </td>
                        {hasActions && (
                          <ActionMenuWrapper>
                            <ActionMenu
                              actions={getPermittedActions(linkedThirdparty)}
                              name={`Manage ${linkedThirdparty.id}`}
                              ellipsisColour={theme.colours.white}
                              renderIcon={<UnlinkIcon aria-hidden />}
                            />
                          </ActionMenuWrapper>
                        )}
                      </>
                    ) : (
                      <LinkedThirdPartyHiddenRow
                        onClickThirdPartyName={() =>
                          handleLinkedThirdPartyNameClick(linkedThirdparty)
                        }
                      />
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </SimpleTable>
          <Pagination
            setCurrentPage={setCurrentPage}
            totalPages={linkedThirdpartiesData?.pageCount}
            currentPage={currentPage}
            onPageChange={(change) => {
              setCurrentPage(change.currentPage);
            }}
            dark
          />
        </TableWithPaginationContainer>
      )}
      {isOpenCreateModal && (
        <ThirdPartyFormModal
          isOpen={true}
          onClose={handleOnCloseModal}
          onSuccess={handleThirdPartyCreated}
          isChild={true}
        />
      )}
      {canDeleteRelatedEntities && (
        <ThirdpartyUnlinkModal
          isOpen={unlinkModalOpen}
          onClose={closeUnlinkModal}
          linkThirdParty={thirdpartyToBeUnlink}
          parentThirdPartyId={id}
          onUnlinked={() => fetchLinkedThirdparties()}
        />
      )}
      <LinkThirdpartyModal
        title={`Link a ${terminologyConfig?.thirdPartySingularNaming} to ${thirdParty.name}`}
        mainThirdparty={thirdParty}
        mainThirdPartyId={id}
        activeThirdPartyInternalId={linkModalState.internalId}
        isOpen={linkModalState.isOpen}
        onClose={closeLinkThirdpartyModal}
        onLinked={handleOnThirdPartyLinked}
        linkedThirdPartyConfigurations={linkedThirdPartyConfigurations}
      />
      <ConfirmationModal
        isOpen={isInitiateComplianceCheckModalOpen}
        onClose={handleInitiateComplianceCheckOnCancel}
        onProceed={handleInitiateComplianceCheckOnProceed}
        onCancel={handleInitiateComplianceCheckOnCancel}
        title="Initiate compliance check?"
        message={`The ${terminologyConfig?.thirdPartySingularNaming} has been successfully created. Would you like to initiate a compliance check?`}
        proceedButtonText="Initiate"
        cancelButtonText="Not now"
        proceedButtonLoading={false}
        onProceedAriaLabel="Initiate"
        loading={false}
        cancelButtonProps={{ text: true, style: { width: 'fit-content' } }}
      />
    </LinkedThirdPartiesComponent>
  );
};

export default LinkedThirdparties;
